// Cloud sync: keeps the signed-in account's copy of every store in step with this device.
// Each persisted store is one document; the newest updatedAt wins (src/logic/cloudSync.ts). A store
// takes part by being listed in DOCS: its own persist options say what is saved, its version and
// how to migrate an older copy, so a new store or a new field needs no sync code. Photos stay on
// the device for now.
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from './storage';
import { useFriends } from './friends';
import { useAccount } from './account';
import { useLibrary } from './library';
import { usePlanner } from './planner';
import { useShopping } from './shopping';
import { useProgress } from './progress';
import { useSettings } from './settings';
import { MERGE_PREFIX, MERGE_WINNER, mergeDocs, mergeLibraries, plan, PUSH_DELAY_MS } from '../logic/cloudSync';
import * as cloud from '../platform/cloud';

type Persisted = {
  getState: () => object;
  setState: (s: object) => void;
  subscribe: (cb: (s: object, prev: object) => void) => () => void;
  persist: { getOptions: () => { version?: number; partialize?: (s: never) => unknown; migrate?: (s: unknown, v: number) => unknown } };
};
type Doc = {
  name: string; store: Persisted; stamp: (s: never) => number; deviceOnly?: string[];
  /** Marks merged data as changed now, where the document keeps its time somewhere other than updatedAt. */
  touch?: (data: Record<string, unknown>, now: number) => Record<string, unknown>;
};

/**
 * Settings that never leave the device: body measures, allergies and diets (health data), holiday
 * traditions and calendar (they can reveal a religion). The privacy policy promises this.
 */
export const SETTINGS_DEVICE_ONLY = ['isSuper', 'superExpiresAt', 'dietProfile', 'allergies', 'diets', 'traditions', 'calendar'];

const DOCS: Doc[] = [
  { name: 'library', store: useLibrary as unknown as Persisted, stamp: (s: { updatedAt: number }) => s.updatedAt },
  { name: 'planner', store: usePlanner as unknown as Persisted, stamp: (s: { updatedAt: number }) => s.updatedAt },
  {
    name: 'shopping', store: useShopping as unknown as Persisted, stamp: (s: { grocery: { updatedAt: number }; pantry: { updatedAt: number } }) => Math.max(s.grocery.updatedAt, s.pantry.updatedAt),
    touch: (d, now) => ({ ...d, grocery: { ...(d.grocery as object), updatedAt: now }, pantry: { ...(d.pantry as object), updatedAt: now } }),
  },
  { name: 'progress', store: useProgress as unknown as Persisted, stamp: (s: { updatedAt: number }) => s.updatedAt },
  // Never synced: Super (each device asks its store or the server) and everything sensitive, see below.
  { name: 'settings', store: useSettings as unknown as Persisted, stamp: (s: { updatedAt: number }) => s.updatedAt, deviceOnly: SETTINGS_DEVICE_ONLY },
];
const LIBRARY_KINDS = ['recipes', 'cookbooks', 'mealplans', 'units', 'ingredients', 'utensils'] as const;

export type CloudStatus = 'off' | 'syncing' | 'synced' | 'error';

type CloudState = {
  /** The account this device last synced with; another or null = joining (the account's copy wins, libraries merge). */
  accountId: string | null;
  status: CloudStatus;
  lastSyncAt: number | null;
  /** Error code from the server (quota_exceeded, rate_limited, …) or 'offline'. */
  error: string | null;
};

export const useCloud = create<CloudState>()(
  persist((): CloudState => ({ accountId: null, status: 'off', lastSyncAt: null, error: null }), {
    name: 'cloud', storage: persistStorage, version: 1, partialize: s => ({ accountId: s.accountId, lastSyncAt: s.lastSyncAt }),
  }),
);

const stampOf = (d: Doc) => d.stamp(d.store.getState() as never);

function snapshot(d: Doc): string {
  const o = d.store.persist.getOptions();
  const data = { ...((o.partialize ? o.partialize(d.store.getState() as never) : d.store.getState()) as Record<string, unknown>) };
  for (const k of d.deviceOnly ?? []) delete data[k];
  return JSON.stringify({ v: o.version ?? 0, data }); // functions drop out of JSON
}

/** The document's data in this app version's shape, or null if a newer app version wrote it. */
function readDoc(d: Doc, json: string): Record<string, unknown> | null {
  const { v, data } = JSON.parse(json) as { v: number; data: Record<string, unknown> };
  const o = d.store.persist.getOptions();
  if (v > (o.version ?? 0)) return null;
  return (v < (o.version ?? 0) && o.migrate ? o.migrate(data, v) : data) as Record<string, unknown>;
}

let applying = false;
function apply(d: Doc, data: Record<string, unknown>): void {
  const keep = Object.fromEntries((d.deviceOnly ?? []).map(k => [k, (d.store.getState() as Record<string, unknown>)[k]]));
  applying = true;
  try {
    d.store.setState({ ...data, ...keep });
  } finally {
    applying = false;
  }
}

async function pullInto(d: Doc): Promise<void> {
  const data = readDoc(d, (await cloud.pull(d.name)).json);
  if (data) apply(d, data);
}

let running: Promise<void> | null = null;
let again = false;

/** Pulls what is newer on the server, pushes what is newer here. Safe to call any time. */
export function syncNow(): Promise<void> {
  if (running) {
    again = true;
    return running;
  }
  running = run().finally(() => {
    running = null;
    if (again) {
      again = false;
      syncNow();
    }
  });
  return running;
}

async function run(): Promise<void> {
  const user = useAccount.getState().user;
  if (!cloud.cloudAvailable || !user) return;
  useCloud.setState({ status: 'syncing', error: null });
  try {
    const remote = await cloud.manifest();
    const joining = useCloud.getState().accountId !== user.id;
    const { pull: toPull, push: toPush } = plan(DOCS.map(d => ({ name: d.name, updatedAt: stampOf(d) })), remote, joining);
    const library = DOCS[0];
    // A device that never synced, joining while both sides have a library: merge instead of replacing
    // either. (Switching to another account takes that account's library as it is: no mixing.)
    if (useCloud.getState().accountId === null && stampOf(library) > 0 && remote.some(r => r.name === 'library')) {
      const theirs = readDoc(library, (await cloud.pull('library')).json);
      if (theirs) {
        const mine = JSON.parse(snapshot(library)).data;
        apply(library, mergeLibraries(mine, theirs as typeof mine, LIBRARY_KINDS, Date.now()));
        toPull.splice(0, toPull.length, ...toPull.filter(n => n !== 'library'));
        if (!toPush.includes('library')) toPush.push('library');
      }
    }
    for (const name of toPull) await pullInto(DOCS.find(d => d.name === name)!);
    for (const name of toPush) {
      const d = DOCS.find(x => x.name === name)!;
      if ((await cloud.push(name, stampOf(d), snapshot(d))) === 'stale') await pullInto(d);
    }
    if (remote.some(r => r.name === MERGE_WINNER)) await mergeParked(remote.map(r => r.name));
    useCloud.setState({ accountId: user.id, status: 'synced', lastSyncAt: Date.now() });
  } catch (e) {
    useCloud.setState({ status: 'error', error: e instanceof cloud.CloudError ? e.code : 'offline' });
  }
}

/**
 * After two accounts were joined (merge.ts on the server), the joining account's documents wait as
 * merge-<name>: merge each into this device's copy (every item kept, the chosen side wins clashes),
 * upload the result and remove the parked copies. Whichever device syncs first does it, once.
 */
async function mergeParked(names: string[]): Promise<void> {
  const { incomingWins } = JSON.parse((await cloud.pull(MERGE_WINNER)).json) as { incomingWins: boolean };
  for (const parked of names.filter(n => n.startsWith(MERGE_PREFIX) && n !== MERGE_WINNER)) {
    const d = DOCS.find(x => x.name === parked.slice(MERGE_PREFIX.length));
    const incoming = d ? readDoc(d, (await cloud.pull(parked)).json) : null;
    if (d && incoming) {
      const now = Date.now();
      const merged = mergeDocs(d.name, JSON.parse(snapshot(d)).data, incoming, incomingWins, now);
      apply(d, d.touch ? d.touch(merged, now) : merged);
      await cloud.push(d.name, stampOf(d), snapshot(d));
    }
    await cloud.remove(parked);
  }
  await cloud.remove(MERGE_WINNER);
}

let timer: ReturnType<typeof setTimeout> | null = null;
function schedule(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    syncNow();
  }, PUSH_DELAY_MS);
}

let started = false;
/** Called once at launch: syncs on sign-in, a few seconds after each change, and when the page hides or shows. */
export function startCloudSync(): void {
  if (started || !cloud.cloudAvailable) return;
  started = true;
  useAccount.subscribe((s, prev) => {
    if (s.user && useFriends.getState().owner && useFriends.getState().owner !== s.user.id) useFriends.getState().forget();
    if (s.user && s.user.id !== prev.user?.id) syncNow();
    if (!s.user && prev.user) useCloud.setState({ status: 'off', error: null });
  });
  for (const d of DOCS) {
    d.store.subscribe((s, prev) => {
      if (!applying && useAccount.getState().user && d.stamp(s as never) !== d.stamp(prev as never)) schedule();
    });
  }
  cloud.onVisibility(() => {
    if (!useAccount.getState().user) return;
    if (timer) clearTimeout(timer);
    timer = null;
    syncNow(); // hidden: push now; visible again: pull what other devices changed
  });
}

/** Deletes the account's cloud copy; this device keeps its data. */
export async function deleteCloudCopy(): Promise<void> {
  await cloud.wipe();
  useCloud.setState({ accountId: null, lastSyncAt: null });
}
